import { createReducer } from "@reduxjs/toolkit";
import {
    INotebook,
    INote,
    loadNotebooks,
    createNotebook,
    updateNotebook,
    deleteNotebook,
    setCurrentNotebook,
    loadNotes,
    loadNoteDetails,
    createNote,
    updateNote,
    deleteNote,
    createNoteBlock,
    updateNoteBlock,
    deleteNoteBlock,
    reorderBlocks,
} from "../actions/notebooks";
import { resolveRsaaErrorMessage } from "../utils/rsaaErrorMessage";

export interface INotebooksState {
    notebooks: INotebook[];
    notes: INote[];
    currentNotebook: string | null;
    loading: boolean;
    error: string | null;
}

const initialState: INotebooksState = {
    notebooks: [],
    notes: [],
    currentNotebook: null,
    loading: false,
    error: null,
};

export const notebooks = createReducer(initialState, (builder) => {
    builder
        // Load notebooks
        .addCase(loadNotebooks.request, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(loadNotebooks.success, (state, action) => {
            state.loading = false;
            state.notebooks = action.payload;
        })
        .addCase(loadNotebooks.error, (state, action) => {
            state.loading = false;
            state.error = resolveRsaaErrorMessage(
                action.payload,
                "Failed to load notebooks"
            );
        })

        // Create notebook
        .addCase(createNotebook.request, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(createNotebook.success, (state, action) => {
            state.loading = false;
            state.notebooks.push(action.payload);
        })
        .addCase(createNotebook.error, (state, action) => {
            state.loading = false;
            state.error = resolveRsaaErrorMessage(
                action.payload,
                "Failed to create notebook"
            );
        })

        // Update notebook
        .addCase(updateNotebook.request, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(updateNotebook.success, (state, action) => {
            state.loading = false;
            const index = state.notebooks.findIndex(
                (n) => n.ui_id === action.payload.ui_id
            );
            if (index !== -1) {
                state.notebooks[index] = action.payload;
            }
        })
        .addCase(updateNotebook.error, (state, action) => {
            state.loading = false;
            state.error = resolveRsaaErrorMessage(
                action.payload,
                "Failed to update notebook"
            );
        })

        // Delete notebook
        .addCase(deleteNotebook.request, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(deleteNotebook.success, (state) => {
            state.loading = false;
            // Note: We'll need to reload notebooks or track the deleted ID
        })
        .addCase(deleteNotebook.error, (state, action) => {
            state.loading = false;
            state.error = resolveRsaaErrorMessage(
                action.payload,
                "Failed to delete notebook"
            );
        })

        // Set current notebook
        .addCase(setCurrentNotebook, (state, action) => {
            state.currentNotebook = action.payload;
        })

        // Load notes
        .addCase(loadNotes.request, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(loadNotes.success, (state, action) => {
            state.loading = false;

            // Ensure all notes have blocks array initialized
            const newNotes = action.payload.map((note) => ({
                ...note,
                blocks: note.blocks || [],
            }));

            // Merge with existing notes, replacing notes from the same notebook
            // First, remove any existing notes from this notebook
            const notebookId =
                newNotes.length > 0 ? newNotes[0].notebook_ui_id : null;
            if (notebookId) {
                state.notes = state.notes.filter(
                    (note) => note.notebook_ui_id !== notebookId
                );
                // Then add the new notes
                state.notes.push(...newNotes);
            }
        })
        .addCase(loadNotes.error, (state, action) => {
            state.loading = false;
            state.error = resolveRsaaErrorMessage(
                action.payload,
                "Failed to load notes"
            );
        })

        // Load note details (with blocks)
        .addCase(loadNoteDetails.request, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(loadNoteDetails.success, (state, action) => {
            state.loading = false;

            // Update the existing note in the notes array with the detailed data (including blocks)
            const index = state.notes.findIndex(
                (note) => note.ui_id === action.payload.ui_id
            );
            if (index !== -1) {
                state.notes[index] = action.payload;
            } else {
                // If note doesn't exist in the array, add it
                state.notes.push(action.payload);
            }
        })
        .addCase(loadNoteDetails.error, (state, action) => {
            state.loading = false;
            state.error = resolveRsaaErrorMessage(
                action.payload,
                "Failed to load note details"
            );
        })

        // Create note
        .addCase(createNote.request, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(createNote.success, (state, action) => {
            state.loading = false;
            const newNote = { ...action.payload };
            // Ensure blocks array is initialized
            if (!newNote.blocks) {
                newNote.blocks = [];
            }
            state.notes.push(newNote);
        })
        .addCase(createNote.error, (state, action) => {
            state.loading = false;
            state.error = resolveRsaaErrorMessage(
                action.payload,
                "Failed to create note"
            );
        })

        // Update note
        .addCase(updateNote.request, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(updateNote.success, (state, action) => {
            state.loading = false;
            const index = state.notes.findIndex(
                (n) => n.ui_id === action.payload.ui_id
            );
            if (index !== -1) {
                state.notes[index] = action.payload;
            }
        })
        .addCase(updateNote.error, (state, action) => {
            state.loading = false;
            state.error = resolveRsaaErrorMessage(
                action.payload,
                "Failed to update note"
            );
        })

        // Delete note
        .addCase(deleteNote.request, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(deleteNote.success, (state) => {
            state.loading = false;
            // Note: We'll need to reload notes or track the deleted ID
        })
        .addCase(deleteNote.error, (state, action) => {
            state.loading = false;
            state.error = resolveRsaaErrorMessage(
                action.payload,
                "Failed to delete note"
            );
        })

        // Create note block
        .addCase(createNoteBlock.request, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(createNoteBlock.success, (state) => {
            state.loading = false;
            // For now, we'll rely on the component to reload notes
            // The proper fix would be to include noteId in the payload
        })
        .addCase(createNoteBlock.error, (state, action) => {
            state.loading = false;
            state.error = resolveRsaaErrorMessage(
                action.payload,
                "Failed to create note block"
            );
        })

        // Update note block
        .addCase(updateNoteBlock.request, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(updateNoteBlock.success, (state) => {
            state.loading = false;
            // For now, we'll rely on the component to handle updates locally
        })
        .addCase(updateNoteBlock.error, (state, action) => {
            state.loading = false;
            state.error = resolveRsaaErrorMessage(
                action.payload,
                "Failed to update note block"
            );
        })

        // Delete note block
        .addCase(deleteNoteBlock.request, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(deleteNoteBlock.success, (state) => {
            state.loading = false;
            // For now, we'll rely on the component to reload notes
        })
        .addCase(deleteNoteBlock.error, (state, action) => {
            state.loading = false;
            state.error = resolveRsaaErrorMessage(
                action.payload,
                "Failed to delete note block"
            );
        })

        // Reorder blocks
        .addCase(reorderBlocks.request, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(reorderBlocks.success, (state, action) => {
            state.loading = false;

            // Handle the case where the API returns block positions (current implementation)
            if (
                action.payload &&
                action.payload.blocks &&
                Array.isArray(action.payload.blocks)
            ) {
                // Since we don't have the noteId in the response, we need to find the note
                // that contains these blocks by matching block IDs
                const blockIds = action.payload.blocks.map(
                    (b) => b.block_ui_id
                );

                // Find the note that contains these blocks
                const noteIndex = state.notes.findIndex(
                    (note) =>
                        note.blocks &&
                        note.blocks.some((block) =>
                            blockIds.includes(block.ui_id)
                        )
                );

                if (noteIndex !== -1) {
                    const note = state.notes[noteIndex];

                    // Create a map of block_ui_id to new position
                    const positionMap = new Map(
                        action.payload.blocks.map((block) => [
                            block.block_ui_id,
                            block.position,
                        ])
                    );

                    // Update the positions of existing blocks
                    note.blocks = note.blocks.map((block) => ({
                        ...block,
                        position:
                            positionMap.get(block.ui_id) ?? block.position,
                    }));
                }
            }
        })
        .addCase(reorderBlocks.error, (state, action) => {
            state.loading = false;
            state.error = resolveRsaaErrorMessage(
                action.payload,
                "Failed to reorder blocks"
            );
        });
});
